import React, { useCallback, useEffect, useState } from 'react';
import axios from '../../api/axiosInstance';
import { API_BASE } from '../../api/config';

function formatConfidence(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'n/a';
  return value.toFixed(2);
}

function getExecutionTone(decision = '') {
  if (decision === 'ALLOW') return 'good';
  if (decision === 'COMPLETED') return 'good';
  if (decision === 'BLOCK') return 'alert';
  if (decision === 'CONFIRM') return 'caution';
  if (decision === 'CANCELLED') return 'muted';
  if (decision === 'IDLE') return 'muted';
  return 'muted';
}

function getSignalLabel(signal = '') {
  switch (signal) {
    case 'emotionalDisclosure':
      return 'Emotional Disclosure';
    case 'entityActivation':
      return 'Entity Activation';
    case 'pendingAnswer':
      return 'Pending Answer';
    case 'selectionDecline':
      return 'Soft Decline';
    case 'closure':
      return 'Closure';
    case 'pronounResolution':
      return 'Pronoun Continuity';
    case 'none':
    default:
      return 'None';
  }
}

function getReplyModeLabel(mode = '') {
  switch (mode) {
    case 'acknowledge_and_followup':
      return 'Acknowledge + Follow-up';
    case 'close_or_redirect':
      return 'Close or Redirect';
    case 'answer_pending_question':
      return 'Answer Pending Question';
    case 'preserve_continuity':
      return 'Preserve Continuity';
    case 'default':
    default:
      return 'Default';
  }
}

function getFrameLabel(frameType = '') {
  if (!frameType) return 'n/a';

  return String(frameType)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getStatusLabel(identity = null) {
  if (!identity?.status) return 'n/a';

  return String(identity.status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getReferenceLabel(reference = null) {
  if (!reference?.name) return 'None';

  if (reference?.role) {
    return `${reference.name} (${reference.role})`;
  }

  return reference.name;
}

function getTopicLabel(topic = null) {
  if (!topic?.topic) return 'None';
  return topic.topic;
}

function getHumanReadableReason(arbitration = {}) {
  const signal = arbitration?.dominantSignal || 'none';

  switch (signal) {
    case 'emotionalDisclosure':
      return 'The system recognized meaningful emotional content and prioritized a caring response.';
    case 'entityActivation':
      return 'The system detected a strong person-reference signal and kept that person in active focus.';
    case 'pendingAnswer':
      return 'The system recognized that the user was primarily answering the prior question.';
    case 'selectionDecline':
      return 'The system detected a soft decline and treated it as a lighter conversational move.';
    case 'closure':
      return 'The system recognized a closure signal and reduced follow-up pressure.';
    case 'pronounResolution':
      return 'The system preserved continuity by binding the pronoun to the active compatible person already in frame.';
    default:
      return 'No dominant special signal was detected on this turn.';
  }
}

function getSectionStyle() {
  return {
    padding: '10px 12px',
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.65)',
    marginBottom: '10px',
  };
}

function getPanelStyle() {
  return {
    padding: '10px',
    border: '1px solid rgba(0,0,0,0.16)',
    borderRadius: '14px',
    background: 'linear-gradient(180deg, rgba(248,248,255,0.95) 0%, rgba(240,240,250,0.95) 100%)',
  };
}

function getBadgeStyle(kind = 'neutral') {
  const styles = {
    neutral: {
      background: 'rgba(0,0,0,0.06)',
      color: '#444',
      border: '1px solid rgba(0,0,0,0.08)',
    },
    muted: {
      background: 'rgba(0,0,0,0.035)',
      color: '#666',
      border: '1px solid rgba(0,0,0,0.06)',
    },
    good: {
      background: 'rgba(46, 125, 50, 0.12)',
      color: '#1b5e20',
      border: '1px solid rgba(46, 125, 50, 0.24)',
    },
    caution: {
      background: 'rgba(245, 124, 0, 0.12)',
      color: '#8a4b00',
      border: '1px solid rgba(245, 124, 0, 0.24)',
    },
    alert: {
      background: 'rgba(198, 40, 40, 0.10)',
      color: '#8e1c1c',
      border: '1px solid rgba(198, 40, 40, 0.24)',
    },
    active: {
      background: 'rgba(25, 118, 210, 0.12)',
      color: '#0d47a1',
      border: '1px solid rgba(25, 118, 210, 0.24)',
    },
    continuity: {
      background: 'rgba(123, 31, 162, 0.12)',
      color: '#5e2a84',
      border: '1px solid rgba(123, 31, 162, 0.24)',
    },
  };

  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1.2,
    ...styles[kind],
  };
}

function getScoreTone(value = 0) {
  if (value >= 0.75) return 'good';
  if (value >= 0.45) return 'caution';
  if (value > 0) return 'alert';
  return 'muted';
}

function getYesNoTone(value) {
  return value ? 'good' : 'muted';
}

function getDominantSignalTone(signal = '') {
  if (signal === 'emotionalDisclosure') return 'active';
  if (signal === 'pronounResolution') return 'continuity';
  if (signal === 'entityActivation') return 'good';
  if (signal === 'selectionDecline' || signal === 'closure') return 'caution';
  if (signal === 'none' || !signal) return 'muted';
  return 'neutral';
}

function getReplyModeTone(mode = '') {
  if (mode === 'acknowledge_and_followup') return 'active';
  if (mode === 'preserve_continuity') return 'continuity';
  if (mode === 'close_or_redirect') return 'caution';
  if (mode === 'default' || !mode) return 'muted';
  return 'neutral';
}

function getDisplayExecutionDecision(executionDecision = null) {
  if (!executionDecision) {
    return {
      decision: 'IDLE',
      intent: 'none',
      category: 'none',
      operation: 'none',
      target: '—',
      reason: 'No execution boundary action is active.',
    };
  }

  return {
    decision: executionDecision.displayDecision || executionDecision.decision || 'IDLE',
    intent: executionDecision.intent || 'none',
    category: executionDecision.category || 'none',
    operation: executionDecision.operation || 'none',
    target: executionDecision.target || '—',
    reason: executionDecision.reason || '—',
  };
}

function TooltipLabel({ label, tooltip }) {
  if (!tooltip) {
    return <strong>{label}</strong>;
  }

  return (
    <strong title={tooltip} style={{ cursor: 'help', textDecoration: 'underline dotted' }}>
      {label}
    </strong>
  );
}

function SignalRow({
  label,
  value,
  tooltip = '',
  badgeKind = null,
}) {
  return (
    <div style={{ marginBottom: '6px' }}>
      <TooltipLabel label={label} tooltip={tooltip} />:{' '}
      {badgeKind ? (
        <span style={getBadgeStyle(badgeKind)}>{value}</span>
      ) : (
        <span>{value}</span>
      )}
    </div>
  );
}

function MetricRow({
  label,
  value,
  tooltip = '',
}) {
  const numeric = typeof value === 'number' ? value : Number(value);
  const safeValue = Number.isNaN(numeric) ? 0 : numeric;
  const badgeKind = getScoreTone(safeValue);

  return (
    <SignalRow
      label={label}
      value={formatConfidence(safeValue)}
      tooltip={tooltip}
      badgeKind={badgeKind}
    />
  );
}

function buildNarrative(arbitration = {}, reference = {}, executionDisplay = null) {
  if (executionDisplay?.decision === 'CONFIRM') {
    return 'Execution boundary is waiting for explicit authority. No memory edit is admissible until the user gives a clear yes or no.';
  }

  if (executionDisplay?.decision === 'COMPLETED') {
    return 'Execution boundary allowed the requested memory edit after explicit confirmation, and the state change completed.';
  }

  if (executionDisplay?.decision === 'CANCELLED') {
    return 'Execution boundary cancelled the pending memory edit. No state change was made.';
  }

  if (executionDisplay?.decision === 'BLOCK') {
    return 'Execution boundary blocked the requested action because it was not admissible.';
  }

  const dominantSignal = arbitration?.dominantSignal || 'none';
  const activeReference = reference?.lastReferencedEntity?.name || null;

  if (dominantSignal === 'pronounResolution' && activeReference) {
    return `Continuity held: the system kept ${activeReference} active instead of allowing a weaker fallback interpretation to take over.`;
  }

  if (dominantSignal === 'emotionalDisclosure') {
    return 'The system elevated emotional meaning over weaker surface cues, which allowed the response to stay human and context-aware.';
  }

  if (dominantSignal === 'entityActivation' && activeReference) {
    return `The system identified ${activeReference} as the active person in frame and kept the response attached to that relationship.`;
  }

  if (dominantSignal === 'pendingAnswer') {
    return 'The system treated the message primarily as an answer to the prior question and kept the response focused on that exchange.';
  }

  if (dominantSignal === 'selectionDecline') {
    return 'The system detected a lighter decline signal and avoided overcommitting to a stronger interpretation.';
  }

  if (dominantSignal === 'closure') {
    return 'The system recognized a closure pattern and reduced follow-up pressure rather than pushing the thread further.';
  }

  return 'The system is monitoring context, continuity, and competing signals to select the most admissible response path.';
}

function softenDisplayValue(value, fallback = '—') {
  if (value === null || value === undefined) return fallback;

  const normalized = String(value).trim().toLowerCase();

  if (!normalized) return fallback;
  if (normalized === 'none') return fallback;
  if (normalized === 'default') return fallback;
  if (normalized === 'n/a') return fallback;

  return value;
}

function isMeaningfulValue(value) {
  const softened = softenDisplayValue(value, '');
  return String(softened).trim() !== '';
}

function getResponsePolicyTone(decision = '') {
  if (decision === 'ALLOW') return 'good';
  if (decision === 'TRANSFORM') return 'caution';
  if (decision === 'REJECT' || decision === 'REGENERATE') return 'alert';
  return 'muted';
}

function getPolicyStageDisplay(policyResult = null, fallbackStage = '—') {
  const validation = policyResult?.validation || {};
  const effectivePolicy = policyResult?.effectivePolicy || {};
  const rules = effectivePolicy?.rules || {};
  const violations = Array.isArray(validation?.violations)
    ? validation.violations
    : [];

  const candidateResponse = validation?.candidateResponse || '';
  const finalResponse = policyResult?.response || '';
  const decision = validation?.decision || 'IDLE';

  return {
    stage: policyResult?.stage || fallbackStage,
    decision,
    policyId: validation?.policyId || effectivePolicy?.policyId || '—',
    policySource: validation?.policySource || effectivePolicy?.source || '—',
    policyAuthority: validation?.policyAuthority || effectivePolicy?.authority || '—',
    questionAuthority: rules?.questionAuthority || '—',
    allowOptionalQuestion:
      typeof rules?.allowOptionalQuestion === 'boolean'
        ? rules.allowOptionalQuestion
        : null,
    violations,
    candidateResponse,
    finalResponse,
    transformed:
      decision === 'TRANSFORM' ||
      (candidateResponse && finalResponse && candidateResponse !== finalResponse),
  };
}

function getResponsePolicyFlowDisplay(responsePolicyResult = null) {
  const isComposite =
    !!responsePolicyResult?.responseShaper ||
    !!responsePolicyResult?.chatEmit ||
    Object.prototype.hasOwnProperty.call(responsePolicyResult || {}, 'originalCandidate');

  if (!isComposite) {
    const legacyStage = getPolicyStageDisplay(
      responsePolicyResult,
      responsePolicyResult?.stage || 'chat_emit'
    );

    return {
      originalCandidate: legacyStage.candidateResponse,
      shapedResponse: legacyStage.finalResponse,
      emittedResponse: legacyStage.finalResponse,
      responseShaper: null,
      chatEmit: legacyStage,
      postAdmissibilityModified: false,
      trace: [],
    };
  }

  return {
    originalCandidate: responsePolicyResult?.originalCandidate || '',
    shapedResponse: responsePolicyResult?.shapedResponse || '',
    emittedResponse: responsePolicyResult?.emittedResponse || '',
    responseShaper: responsePolicyResult?.responseShaper
      ? getPolicyStageDisplay(responsePolicyResult.responseShaper, 'response_shaper')
      : null,
    chatEmit: getPolicyStageDisplay(responsePolicyResult?.chatEmit, 'chat_emit'),
    postAdmissibilityModified:
      responsePolicyResult?.postAdmissibilityModified === true,
    trace: Array.isArray(responsePolicyResult?.trace)
      ? responsePolicyResult.trace
      : [],
  };
}

function PolicyViolationList({ violations = [] }) {
  if (!violations.length) return null;

  return violations.map((violation, index) => (
    <div
      key={`${violation?.rule || 'violation'}-${violation?.sentenceIndex ?? index}`}
      style={{
        marginTop: '8px',
        padding: '8px 10px',
        borderRadius: '10px',
        border: '1px solid rgba(198, 40, 40, 0.18)',
        background: 'rgba(198, 40, 40, 0.06)',
      }}
    >
      <div><strong>Rule:</strong> {violation?.rule || '—'}</div>
      <div><strong>Action:</strong> {violation?.action || '—'}</div>
      <div><strong>Reason:</strong> {violation?.reason || '—'}</div>
      <div><strong>Sentence:</strong> {violation?.sentence || '—'}</div>
    </div>
  ));
}

function PolicyStageSummary({ title, display = null }) {
  if (!display) return null;

  return (
    <div
      style={{
        marginTop: '10px',
        padding: '10px',
        borderRadius: '10px',
        border: '1px solid rgba(0,0,0,0.10)',
        background: 'rgba(255,255,255,0.55)',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: '8px' }}>{title}</div>
      <SignalRow
        label="Boundary"
        value={display.stage}
        badgeKind="active"
      />
      <SignalRow
        label="Decision"
        value={display.decision}
        badgeKind={getResponsePolicyTone(display.decision)}
      />
      <SignalRow label="Policy" value={display.policyId} />
      <SignalRow label="Policy Source" value={display.policySource} />
      <SignalRow label="Policy Authority" value={display.policyAuthority} />
      <SignalRow label="Question Authority" value={display.questionAuthority} />
      <SignalRow
        label="Optional Questions Allowed"
        value={
          display.allowOptionalQuestion === null
            ? '—'
            : display.allowOptionalQuestion
              ? 'Yes'
              : 'No'
        }
        badgeKind={
          display.allowOptionalQuestion === null
            ? 'muted'
            : display.allowOptionalQuestion
              ? 'good'
              : 'caution'
        }
      />
      <SignalRow
        label="Transformation Applied"
        value={display.transformed ? 'Yes' : 'No'}
        badgeKind={display.transformed ? 'caution' : 'good'}
      />
      <SignalRow
        label="Violations"
        value={display.violations.length}
        badgeKind={display.violations.length ? 'alert' : 'good'}
      />
      <PolicyViolationList violations={display.violations} />
    </div>
  );
}

function ResponseTextBlock({ label, value }) {
  return (
    <div style={{ marginTop: '10px' }}>
      <div style={{ fontWeight: 700, marginBottom: '4px' }}>{label}</div>
      <div
        style={{
          padding: '8px 10px',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.10)',
          background: 'rgba(255,255,255,0.72)',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function splitTraceSentences(value = '') {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return [];

  return (normalized.match(/[^.!?]+[.!?]?/g) || [])
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function getTraceChanges(input = '', output = '') {
  const before = splitTraceSentences(input);
  const after = splitTraceSentences(output);

  return {
    removed: before.filter((sentence) => !after.includes(sentence)),
    added: after.filter((sentence) => !before.includes(sentence)),
  };
}

function TraceChangeList({ label, values = [], kind = 'removed' }) {
  if (!values.length) return null;

  const isRemoved = kind === 'removed';

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ fontWeight: 700, marginBottom: '4px' }}>{label}</div>
      {values.map((value, index) => (
        <div
          key={`${kind}-${index}-${value}`}
          style={{
            padding: '7px 9px',
            marginBottom: '5px',
            borderRadius: '8px',
            border: isRemoved
              ? '1px solid rgba(198, 40, 40, 0.20)'
              : '1px solid rgba(46, 125, 50, 0.22)',
            background: isRemoved
              ? 'rgba(198, 40, 40, 0.07)'
              : 'rgba(46, 125, 50, 0.08)',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          <strong>{isRemoved ? '−' : '+'}</strong> {value}
        </div>
      ))}
    </div>
  );
}

function ExecutionTraceStep({ step = {}, index = 0 }) {
  const input = step?.input || '';
  const output = step?.output || '';
  const changed = step?.changed === true || input !== output;
  const changes = getTraceChanges(input, output);

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '12px',
        borderRadius: '12px',
        border: changed
          ? '1px solid rgba(245, 124, 0, 0.30)'
          : '1px solid rgba(46, 125, 50, 0.22)',
        background: changed
          ? 'rgba(245, 124, 0, 0.06)'
          : 'rgba(46, 125, 50, 0.05)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div style={{ fontWeight: 800 }}>
          {index + 1}. {step?.label || step?.id || 'Execution Boundary'}
        </div>
        <span style={getBadgeStyle(changed ? 'caution' : 'good')}>
          {changed ? 'TRANSFORMED' : 'PASSED'}
        </span>
      </div>

      {step?.decision && (
        <SignalRow
          label="Decision"
          value={step.decision}
          badgeKind={getResponsePolicyTone(step.decision)}
        />
      )}
      {step?.authority && (
        <SignalRow label="Authority" value={step.authority} />
      )}
      <SignalRow label="Why" value={step?.reason || '—'} />

      {changed ? (
        <>
          <TraceChangeList
            label="Removed"
            values={changes.removed}
            kind="removed"
          />
          <TraceChangeList
            label="Added"
            values={changes.added}
            kind="added"
          />
          {!changes.removed.length && !changes.added.length && (
            <>
              <ResponseTextBlock label="Before" value={input} />
              <ResponseTextBlock label="After" value={output} />
            </>
          )}
        </>
      ) : (
        <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.72 }}>
          No text change at this boundary.
        </div>
      )}

      <PolicyViolationList violations={step?.violations || []} />
    </div>
  );
}

function CollapsibleSection({ title, children, defaultOpen = false }) {
  return (
    <details open={defaultOpen} style={getSectionStyle()}>
      <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
        {title}
      </summary>
      <div style={{ marginTop: '10px' }}>{children}</div>
    </details>
  );
}


function getGaiaDecisionTone(decision = '') {
  if (decision === 'ADMISSIBLE') return 'good';
  if (decision === 'ADDITIONAL_VALIDATION_REQUIRED') return 'caution';
  if (decision === 'SECOND_REVIEW_REQUIRED') return 'alert';
  return 'muted';
}

function GaiaNexusInteropSection({ state, loading, error, onLoadEvent, onReset }) {
  const activeEvidence = state?.activeEvidence || null;
  const event = activeEvidence?.event || null;
  const execution = state?.execution || {};
  const observationWindow = event?.extensions?.observation_window || {};
  const ledger = Array.isArray(state?.ledger) ? state.ledger : [];

  return (
    <CollapsibleSection title="Gaia Nexus External Evidence Interoperability" defaultOpen={true}>
      <div style={{ marginBottom: '10px' }}>
        <strong>Synthetic interoperability demonstration</strong>
        <div style={{ marginTop: '4px', fontSize: '12px', opacity: 0.78 }}>
          Gaia Nexus supplies structured relational evidence. Samirac independently determines what, if anything, that evidence changes for the current execution contract.
        </div>
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <button type="button" onClick={onReset} disabled={loading}>Baseline</button>
        <button type="button" onClick={() => onLoadEvent('GNX-DE-2026-0001')} disabled={loading}>Event 1</button>
        <button type="button" onClick={() => onLoadEvent('GNX-DE-2026-0003')} disabled={loading}>Event 3</button>
        <button type="button" onClick={() => onLoadEvent('GNX-DE-2026-0002')} disabled={loading}>Event 2</button>
      </div>

      {error && (
        <div style={{ marginBottom: '10px' }}>
          <SignalRow label="Demo Error" value={error} badgeKind="alert" />
        </div>
      )}

      <div
        style={{
          padding: '10px',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.10)',
          background: 'rgba(255,255,255,0.55)',
          marginBottom: '10px',
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>Proposed Execution</div>
        <SignalRow label="Contract" value={execution?.contract_id || '—'} />
        <SignalRow label="Operation" value={execution?.operation || '—'} />
        <SignalRow label="Target" value={execution?.target || '—'} />
        <SignalRow label="Identity" value={execution?.identity || '—'} badgeKind="good" />
        <SignalRow label="Authority" value={execution?.authority || '—'} badgeKind="good" />
        <SignalRow label="Permission" value={execution?.permission || '—'} badgeKind="good" />
        <SignalRow label="Required Evidence" value={execution?.required_evidence || '—'} badgeKind="good" />
      </div>

      <div
        style={{
          padding: '10px',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.10)',
          background: 'rgba(255,255,255,0.55)',
          marginBottom: '10px',
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>Current External Evidence</div>
        <SignalRow label="Source" value={event?.extensions?.source || 'None'} badgeKind={event ? 'active' : 'muted'} />
        <SignalRow label="Detection Event" value={event?.event_id || '—'} />
        <SignalRow label="Schema" value={event?.extensions?.schema_version || '—'} />
        <SignalRow label="Skill" value={event?.skill_id || '—'} />
        <SignalRow label="Severity" value={event?.severity || '—'} />
        <SignalRow
          label="Gaia Routing Metadata"
          value={event?.response_class || '—'}
          tooltip="Gaia Nexus response_class is displayed as emitter-side routing metadata only. It does not drive the Samirac execution decision."
        />
        <SignalRow label="Source Subject" value={event?.subject_ref || '—'} />
        <SignalRow label="Daisy Binding" value={activeEvidence?.bindingType || '—'} />
        <SignalRow label="Evidence Status" value={activeEvidence?.status || '—'} badgeKind={event ? 'good' : 'muted'} />
        <SignalRow label="Data Current To" value={observationWindow?.data_current_to || '—'} />
        {event?.observation && <SignalRow label="Observation" value={event.observation} />}
      </div>

      <div
        style={{
          padding: '10px',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.10)',
          background: 'rgba(255,255,255,0.55)',
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>Samirac Evaluation</div>
        <SignalRow
          label="Decision"
          value={execution?.decision || (loading ? 'LOADING' : '—')}
          badgeKind={getGaiaDecisionTone(execution?.decision)}
        />
        <SignalRow label="Condition" value={execution?.condition || '—'} />
        <SignalRow label="Reason" value={execution?.reason || '—'} />
        <SignalRow
          label="Decision Authority"
          value="Samirac execution contract"
          badgeKind="active"
          tooltip="The downstream decision is Samirac's. Gaia Nexus remains an evidence source and does not determine authority or admissibility."
        />
      </div>

      <div
        style={{
          padding: '10px',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.10)',
          background: 'rgba(255,255,255,0.55)',
          marginTop: '10px',
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: '8px' }}>Integration Ledger</div>
        {ledger.length ? (
          ledger.map((entry, index) => (
            <div
              key={`${entry?.eventType || 'event'}-${entry?.eventId || 'baseline'}-${index}`}
              style={{ marginBottom: '8px', fontSize: '12px' }}
            >
              <strong>{entry?.eventType || '—'}</strong>
              {entry?.eventId ? ` · ${entry.eventId}` : ''}
              <div style={{ opacity: 0.78 }}>{entry?.detail || '—'}</div>
            </div>
          ))
        ) : (
          <div style={{ fontSize: '12px', opacity: 0.7 }}>No interoperability ledger events recorded yet.</div>
        )}
      </div>
    </CollapsibleSection>
  );
}

function SignalDemoPanel({
  conversationState = null,
  activeIdentity = null,
  memory = {},
  relationships = [],
  executionDecision = null,
  responsePolicyResult = null,
}) {
  const [gaiaDemoState, setGaiaDemoState] = useState(null);
  const [gaiaDemoLoading, setGaiaDemoLoading] = useState(false);
  const [gaiaDemoError, setGaiaDemoError] = useState('');

  const refreshGaiaDemo = useCallback(async () => {
    setGaiaDemoLoading(true);
    setGaiaDemoError('');

    try {
      const response = await axios.get(`${API_BASE}/api/integrations/gaia-nexus/demo`);
      setGaiaDemoState(response.data);
    } catch (err) {
      console.error('❌ Gaia Nexus demo state fetch failed:', err);
      setGaiaDemoError('Unable to load Gaia Nexus interoperability demo state.');
    } finally {
      setGaiaDemoLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshGaiaDemo();
  }, [refreshGaiaDemo]);

  const loadGaiaDemoEvent = useCallback(async (eventId) => {
    setGaiaDemoLoading(true);
    setGaiaDemoError('');

    try {
      const response = await axios.post(
        `${API_BASE}/api/integrations/gaia-nexus/demo/load/${eventId}`
      );

      setGaiaDemoState((prev) => ({
        ...(prev || {}),
        activeEvidence: response.data.activeEvidence,
        execution: response.data.execution,
        ledger: response.data.ledger,
      }));
    } catch (err) {
      console.error('❌ Gaia Nexus demo event load failed:', err);
      setGaiaDemoError('Unable to load the selected Gaia Nexus Detection Event.');
    } finally {
      setGaiaDemoLoading(false);
    }
  }, []);

  const resetGaiaDemo = useCallback(async () => {
    setGaiaDemoLoading(true);
    setGaiaDemoError('');

    try {
      const response = await axios.post(`${API_BASE}/api/integrations/gaia-nexus/demo/clear`);

      setGaiaDemoState((prev) => ({
        ...(prev || {}),
        activeEvidence: response.data.activeEvidence,
        execution: response.data.execution,
        ledger: response.data.ledger,
      }));
    } catch (err) {
      console.error('❌ Gaia Nexus demo reset failed:', err);
      setGaiaDemoError('Unable to reset the Gaia Nexus interoperability demo.');
    } finally {
      setGaiaDemoLoading(false);
    }
  }, []);

  const arbitration = conversationState?.turnSignalArbitration || {};
  const scores = arbitration?.scores || {};
  const control = arbitration?.control || {};
  const reference = conversationState?.reference || {};
  const frame = conversationState?.frame || {};
  const lifecycle = conversationState?.lifecycle || {};
  const pendingQuestionState = conversationState?.pendingQuestionState || {};
  const pronounResolutionSignal =
    conversationState?.pronounResolutionSignal || arbitration?.pronounResolutionSignal || {};

  const executionDisplay = getDisplayExecutionDecision(executionDecision);
  const responsePolicyFlow = getResponsePolicyFlowDisplay(responsePolicyResult);

  const rawTopSignal = getSignalLabel(arbitration?.dominantSignal);
  const rawReplyMode = getReplyModeLabel(arbitration?.replyMode);
  const frameLabel = getFrameLabel(frame?.type);
  const identityStatus = getStatusLabel(activeIdentity);

  const rawActiveReference = getReferenceLabel(reference?.lastReferencedEntity);
  const activeTopic = getTopicLabel(reference?.lastReferencedTopic);

  const hasPendingQuestion = !!pendingQuestionState?.pendingQuestion?.text;
  const pendingQuestionText = pendingQuestionState?.pendingQuestion?.text || 'None';

  const pronounList = Array.isArray(pronounResolutionSignal?.pronouns)
    ? pronounResolutionSignal.pronouns.join(', ') || 'None'
    : 'None';

  const pronounCandidate = getReferenceLabel(pronounResolutionSignal?.candidateEntity);
  const pronounMode =
    pronounResolutionSignal?.mode
      ? String(pronounResolutionSignal.mode)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (char) => char.toUpperCase())
      : 'None';

  const narrative = buildNarrative(arbitration, reference, executionDisplay);

  const activeReference = softenDisplayValue(rawActiveReference);
  const topSignal = softenDisplayValue(rawTopSignal);
  const replyMode = softenDisplayValue(rawReplyMode);
  const pendingSummaryValue = hasPendingQuestion ? pendingQuestionText : '—';

  const hasMeaningfulSummary =
    isMeaningfulValue(rawActiveReference) ||
    isMeaningfulValue(rawTopSignal) ||
    isMeaningfulValue(rawReplyMode) ||
    hasPendingQuestion;

  const shouldShowDecisionReason =
    arbitration?.dominantSignal && arbitration?.dominantSignal !== 'none';

  return (
    <div style={getPanelStyle()}>
      <div style={{ marginBottom: '10px' }}>
        <strong>dAIsy Live Signal Panel 2</strong>
      </div>

      <GaiaNexusInteropSection
        state={gaiaDemoState}
        loading={gaiaDemoLoading}
        error={gaiaDemoError}
        onLoadEvent={loadGaiaDemoEvent}
        onReset={resetGaiaDemo}
      />

      <CollapsibleSection title="Response Execution Trace" defaultOpen={true}>
        <ResponseTextBlock
          label="Raw Model Candidate"
          value={responsePolicyFlow.originalCandidate}
        />

        {responsePolicyFlow.trace.length ? (
          responsePolicyFlow.trace.map((step, index) => (
            <ExecutionTraceStep
              key={step?.id || `${step?.label || 'trace'}-${index}`}
              step={step}
              index={index}
            />
          ))
        ) : (
          <div style={{ marginTop: '10px' }}>
            <PolicyStageSummary
              title="Response Shaper Boundary"
              display={responsePolicyFlow.responseShaper}
            />
            <PolicyStageSummary
              title="Chat Emit Boundary"
              display={responsePolicyFlow.chatEmit}
            />
          </div>
        )}

        <ResponseTextBlock
          label="Final Emitted Response"
          value={responsePolicyFlow.emittedResponse}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Memory Execution Boundary" defaultOpen={false}>
        <SignalRow
          label="Decision"
          value={executionDisplay.decision}
          badgeKind={getExecutionTone(executionDisplay.decision)}
          tooltip="Shows the visible execution lifecycle state. Passive background memory logging should not overwrite relationship-edit authority."
        />
        <SignalRow
          label="Intent"
          value={executionDisplay.intent}
        />
        <SignalRow
          label="Category"
          value={executionDisplay.category}
        />
        <SignalRow
          label="Operation"
          value={executionDisplay.operation}
        />
        <SignalRow
          label="Target"
          value={executionDisplay.target}
        />
        <SignalRow
          label="Reason"
          value={executionDisplay.reason}
        />
      </CollapsibleSection>

      <div
        style={{
          padding: '10px 12px',
          border: '1px solid rgba(0,0,0,0.14)',
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.92)',
          marginBottom: '10px',
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: '8px' }}>Live Summary</h4>

        <SignalRow
          label="Active Reference"
          value={activeReference}
          badgeKind={reference?.lastReferencedEntity ? 'good' : 'muted'}
        />
        <SignalRow
          label="Dominant Signal"
          value={topSignal}
          badgeKind={getDominantSignalTone(arbitration?.dominantSignal)}
        />
        <SignalRow
          label="Reply Mode"
          value={replyMode}
          badgeKind={getReplyModeTone(arbitration?.replyMode)}
        />
        <SignalRow
          label="Pending Question"
          value={pendingSummaryValue}
          badgeKind={hasPendingQuestion ? 'active' : 'muted'}
        />

        {!hasMeaningfulSummary && (
          <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>
            Waiting for a stronger turn signal now.
          </div>
        )}
      </div>

      <CollapsibleSection title="Identity & Context" defaultOpen={false}>
        <SignalRow label="User" value={activeIdentity?.displayName || memory?.userName || 'Friend'} />
        <SignalRow
          label="Identity Status"
          value={identityStatus}
          badgeKind={identityStatus === 'Anchored' ? 'good' : 'neutral'}
          tooltip="Shows whether the system has a stable identity anchor for the current user before interpreting the turn."
        />
        <SignalRow
          label="Identity Confidence"
          value={formatConfidence(activeIdentity?.confidence)}
          badgeKind={getScoreTone(activeIdentity?.confidence || 0)}
        />
        <SignalRow label="Known Relationships" value={Array.isArray(relationships) ? relationships.length : 0} />
        <SignalRow
          label="Frame"
          value={frameLabel}
          badgeKind={frame?.type ? 'active' : 'muted'}
          tooltip="The current conversational operating mode. This helps constrain interpretation so the system stays inside the active context rather than drifting."
        />
        <SignalRow
          label="Frame Confidence"
          value={formatConfidence(frame?.confidence)}
          badgeKind={getScoreTone(frame?.confidence || 0)}
        />
        <SignalRow
          label="Fresh Open"
          value={lifecycle?.isFreshOpen ? 'Yes' : 'No'}
          badgeKind={getYesNoTone(lifecycle?.isFreshOpen)}
        />
        <SignalRow
          label="Reset Applied"
          value={lifecycle?.shouldResetEphemeral ? 'Yes' : 'No'}
          badgeKind={getYesNoTone(lifecycle?.shouldResetEphemeral)}
        />
        <SignalRow
          label="Active Topic"
          value={softenDisplayValue(activeTopic)}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Decision Summary" defaultOpen={false}>
        <SignalRow
          label="Dominant Signal"
          value={topSignal}
          badgeKind={getDominantSignalTone(arbitration?.dominantSignal)}
          tooltip="This is the strongest admissible interpretation on the current turn. In a mixed sentence, the system scores competing readings and lets the strongest one govern response behavior."
        />
        <SignalRow
          label="Reply Mode"
          value={replyMode}
          badgeKind={getReplyModeTone(arbitration?.replyMode)}
          tooltip="This is the response posture selected after arbitration. It is how the system turns interpretation into controlled behavior."
        />
        <SignalRow
          label="Why This Won"
          value={shouldShowDecisionReason ? getHumanReadableReason(arbitration) : '—'}
          tooltip="This summarizes the winning interpretation in plain language so the response path is explainable, not opaque."
        />
      </CollapsibleSection>

      <CollapsibleSection title="Signal Competition" defaultOpen={false}>
        <MetricRow label="Emotional Disclosure" value={scores?.emotionalDisclosure ?? 0} />
        <MetricRow label="Entity Activation" value={scores?.entityActivation ?? 0} />
        <MetricRow label="Pending Answer" value={scores?.pendingAnswer ?? 0} />
        <MetricRow label="Soft Decline" value={scores?.selectionDecline ?? 0} />
        <MetricRow label="Closure" value={scores?.closure ?? 0} />
        <MetricRow
          label="Pronoun Continuity"
          value={scores?.pronounResolution ?? 0}
          tooltip="This score reflects whether a pronoun can safely bind to the active person already in frame. When strong, it preserves continuity and suppresses weaker fallback guesses."
        />
      </CollapsibleSection>

      <CollapsibleSection title="Continuity & Safeguards" defaultOpen={false}>
        <SignalRow label="Pronouns Seen" value={softenDisplayValue(pronounList)} />
        <SignalRow label="Pronoun Candidate" value={softenDisplayValue(pronounCandidate)} />
        <SignalRow label="Binding Mode" value={softenDisplayValue(pronounMode)} />
        <SignalRow
          label="Pronoun Resolved"
          value={pronounResolutionSignal?.resolved ? 'Yes' : 'No'}
          badgeKind={getYesNoTone(pronounResolutionSignal?.resolved)}
          tooltip="A deterministic pronoun bind only happens when the active person is compatible and the signal is strong enough to be admissible."
        />
        <SignalRow
          label="Preserve Continuity"
          value={control?.shouldPreservePronounContinuity ? 'Yes' : 'No'}
          badgeKind={control?.shouldPreservePronounContinuity ? 'continuity' : 'muted'}
          tooltip="When enabled, the system keeps the active person in frame instead of letting a weaker interpretation wipe that continuity."
        />
        <SignalRow
          label="Fallback Bypassed"
          value={control?.shouldBypassFallback ? 'Yes' : 'No'}
          badgeKind={control?.shouldBypassFallback ? 'good' : 'muted'}
          tooltip="This prevents weak fallback inference from overriding stronger established context. In demo terms: continuity beats guessing."
        />
        <SignalRow
          label="Carry Forward Entity"
          value={control?.shouldCarryForwardEntity ? 'Yes' : 'No'}
          badgeKind={control?.shouldCarryForwardEntity ? 'good' : 'muted'}
        />
        <SignalRow
          label="Prioritize Emotional Reply"
          value={control?.shouldPrioritizeEmotionalReply ? 'Yes' : 'No'}
          badgeKind={control?.shouldPrioritizeEmotionalReply ? 'active' : 'muted'}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Pending Question" defaultOpen={false}>
        <SignalRow
          label="Open Pending Question"
          value={hasPendingQuestion ? 'Yes' : 'No'}
          badgeKind={getYesNoTone(hasPendingQuestion)}
        />
        <SignalRow label="Question" value={hasPendingQuestion ? pendingQuestionText : '—'} />
        <SignalRow
          label="Answer Detected"
          value={pendingQuestionState?.detectedAnswerToPendingQuestion ? 'Yes' : 'No'}
          badgeKind={getYesNoTone(pendingQuestionState?.detectedAnswerToPendingQuestion)}
        />
        <SignalRow label="Answer Signal" value={softenDisplayValue(pendingQuestionState?.answerSignal || 'none')} />
        <SignalRow
          label="Close Pending Question"
          value={control?.shouldClosePendingQuestion ? 'Yes' : 'No'}
          badgeKind={getYesNoTone(control?.shouldClosePendingQuestion)}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Demo Narrative" defaultOpen={false}>
        <div>{narrative}</div>
      </CollapsibleSection>
    </div>
  );
}

export default SignalDemoPanel;